import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { DeleteModal } from '../../../Common/DeleteModal';
import DetailSection from '../../../Common/DetailSection';
import {
  getFeatureToggle,
  deleteFeatureToggle,
  createOrUpdateFeatureToggle,
  FeatureToggle
} from '../../../api/featureManagement/featureToggleService';
import { displayConditionValue } from '../conditionUtils';
import '../FeatureManagement.css';
import './FeatureToggleDetailPage.css';

const FeatureToggleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: any) => state.Login);
  const clientId = process.env.REACT_APP_CLIENT_ID || user?.clientId || '';
  
  const locationState = location.state as any;
  const isEditMode = locationState?.edit || false;

  const [toggle, setToggle] = useState<FeatureToggle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadToggle();
    }
  }, [id]);

  const loadToggle = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getFeatureToggle(id);
      setToggle(data);
    } catch (err: any) {
      console.error('Error loading feature toggle:', err);
      setError(err.message || 'Failed to load feature toggle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/feature-management/feature-toggles');
  };

  const handleEdit = () => {
    navigate(`/feature-management/feature-toggles/${id}`, {
      state: { toggle, clientId, edit: true }
    });
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!toggle) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteFeatureToggle({
        featureId: toggle.featureId,
        entityType: toggle.entityType,
        entityValue: toggle.entityValue,
      });
      navigate('/feature-management/feature-toggles');
    } catch (err: any) {
      console.error('Error deleting feature toggle:', err);
      setDeleteError(err.message || 'Failed to delete feature toggle. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!toggle) return;

    try {
      setIsToggling(true);
      const enabled = toggle.status !== 'ENABLED';

      const payload = {
        featureName: toggle.featureName || toggle.featureId,
        featureScope: toggle.scope,
        clientId: toggle.clientId || clientId,
        enabled,
      };

      await createOrUpdateFeatureToggle(payload as any);
      await loadToggle();
    } catch (err: any) {
      console.error('Error toggling feature toggle status:', err);
      setError(err.message || 'Failed to update feature toggle status. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const handleRestore = async () => {
    if (!toggle) return;

    try {
      setIsRestoring(true);
      const hasConditions = (toggle.conditions || []).length > 0;
      const payload = {
        featureName: toggle.featureName || toggle.featureId,
        featureScope: toggle.scope,
        clientId: toggle.clientId || clientId,
        entityType: toggle.entityType,
        entityValue: toggle.entityValue,
        enabled: true,
        conditions: hasConditions
          ? (toggle.conditions || []).map((cond: any) => ({
              name: cond.name,
              comparator: cond.comparator || 'EQ',
              value: cond.value,
            }))
          : undefined,
        setConditions: hasConditions,
      };
      await createOrUpdateFeatureToggle(payload as any);
      await loadToggle();
    } catch (err: any) {
      console.error('Error restoring feature toggle:', err);
      setError(err.message || 'Failed to restore feature toggle. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ENABLED':
        return { label: 'Enabled', variant: 'success' as const };
      case 'DISABLED':
        return { label: 'Disabled', variant: 'secondary' as const };
      case 'KILLED':
        return { label: 'Killed', variant: 'danger' as const };
      default:
        return { label: status, variant: 'secondary' as const };
    }
  };

  if (loading) {
    return (
      <Container fluid className="feature-management-page">
        <ShimmerLoader type="table" rows={3} />
      </Container>
    );
  }

  if (error && !toggle) {
    return (
      <Container fluid className="feature-management-page">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={handleBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!toggle) {
    return (
      <Container fluid className="feature-management-page">
        <Alert variant="warning">
          <Alert.Heading>Feature Toggle Not Found</Alert.Heading>
          <p>The feature toggle you're looking for doesn't exist.</p>
          <Button variant="outline-warning" onClick={handleBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="feature-management-page">
      {/* Breadcrumbs */}
      <div className="mb-3" style={{ padding: '0 15px' }}>
        <Breadcrumbs
          items={[
            { label: 'Feature Management', onClick: () => navigate('/feature-management') },
            { label: 'Feature Toggle', onClick: () => navigate('/feature-management/feature-toggles') },
            { label: toggle.featureName || toggle.featureId, active: true }
          ]}
        />
      </div>

      {/* Header */}
      <div style={{ padding: '0 15px', marginBottom: '24px' }}>
        <Button
          variant="link"
          onClick={handleBack}
          className="mb-2 p-0"
          style={{ textDecoration: 'none', color: '#212529' }}
        >
          ← Back to Feature Toggles
        </Button>
        <h2 className="mb-0">{toggle.featureName || toggle.featureId}</h2>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ padding: '0 15px', marginBottom: '16px' }}>
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Killed Badge */}
      {toggle.status === 'KILLED' && (
        <div style={{ padding: '0 15px', marginBottom: '16px' }}>
          <Alert variant="danger">
            <strong>This Feature is Killed.</strong>
          </Alert>
        </div>
      )}

      {/* Details Section */}
      <div style={{ padding: '0 15px' }}>
        <DetailSection
          title="Details"
          status={getStatusBadge(toggle.status)}
          fields={[
            { label: 'ID', value: `#${toggle.id || toggle._id.slice(-6)}` },
            { label: 'Scope', value: toggle.scope },
            { label: 'Entity Value', value: toggle.entityValue },
            { label: 'Feature ID', value: toggle.featureId },
            { label: 'Client ID', value: toggle.clientId },
            { label: 'Status', value: toggle.status },
            { label: 'Feature Name', value: toggle.featureName || toggle.featureId },
            { label: 'Entity Type', value: toggle.entityType }
          ]}
          actions={
            <>
              {toggle.status === 'KILLED' ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleRestore}
                  disabled={isRestoring}
                >
                  {isRestoring ? 'Restoring...' : 'Restore'}
                </Button>
              ) : (
                <Form.Check
                  type="switch"
                  id="status-switch"
                  label={toggle.status === 'ENABLED' ? 'Enabled' : 'Disabled'}
                  checked={toggle.status === 'ENABLED'}
                  onChange={handleToggleStatus}
                  disabled={isToggling}
                  style={{ marginRight: '1rem' }}
                />
              )}
              <Button
                variant="dark"
                size="sm"
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
              >
                Delete
              </Button>
            </>
          }
          conditions={
            toggle.conditions && toggle.conditions.length > 0
              ? {
                  title: 'Conditions',
                  items: toggle.conditions.map((cond) => ({
                    name: cond.name,
                    value: `${(cond as any).comparator ?? ''} ${displayConditionValue(cond.value)}`.trim()
                  }))
                }
              : undefined
          }
        />
      </div>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteError(null);
        }}
        deleteModalFunction={confirmDelete}
        title="Delete Feature Toggle"
        message={`Are you sure you want to delete this feature toggle? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </Container>
  );
};

export default FeatureToggleDetailPage;
